import { PriceData } from './types';
import { startOfWeek, startOfMonth, format, parseISO } from 'date-fns';

export interface AggregatedPeriod {
  date: string;
  priceData: PriceData[];
}

/**
 * Aggregates data based on the selected frequency
 */
export function aggregateDataByFrequency(
  data: PriceData[],
  frequency: 'daily' | 'weekly' | 'monthly'
): PriceData[] {
  if (frequency === 'daily') {
    return data; // No aggregation needed
  }

  const groupedData = frequency === 'weekly' 
    ? groupByWeek(data)
    : groupByMonth(data);

  return flattenAggregatedData(groupedData);
}

/**
 * Groups data by ISO week (Monday-Sunday)
 */
function groupByWeek(data: PriceData[]): Map<string, PriceData[]> {
  const weekMap = new Map<string, PriceData[]>();

  data.forEach(item => {
    const date = parseISO(item.date);
    const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday
    const weekKey = format(weekStart, 'yyyy-MM-dd');

    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, []);
    }
    weekMap.get(weekKey)!.push(item);
  });

  return weekMap;
}

/**
 * Groups data by calendar month
 */
function groupByMonth(data: PriceData[]): Map<string, PriceData[]> {
  const monthMap = new Map<string, PriceData[]>();

  data.forEach(item => {
    const date = parseISO(item.date);
    const monthStart = startOfMonth(date);
    const monthKey = format(monthStart, 'yyyy-MM-dd');

    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, []);
    }
    monthMap.get(monthKey)!.push(item);
  });

  return monthMap;
}

/**
 * Flattens aggregated data by creating averaged price entries
 */
function flattenAggregatedData(groupedData: Map<string, PriceData[]>): PriceData[] {
  const result: PriceData[] = [];

  groupedData.forEach((periodData, date) => {
    // Group by location within the period
    const locationMap = new Map<string, PriceData[]>();
    
    periodData.forEach(item => {
      const key = `${item.location}_${item.source}_${item.product_name}`;
      if (!locationMap.has(key)) {
        locationMap.set(key, []);
      }
      locationMap.get(key)!.push(item);
    });

    // Create averaged entries for each location
    locationMap.forEach((items) => {
      const avgPrice = items.reduce((sum, item) => sum + item.price, 0) / items.length;
      const totalOrders = items.reduce((sum, item) => sum + (item.num_order || 0), 0);
      
      result.push({
        date,
        price: avgPrice,
        location: items[0].location,
        product_name: items[0].product_name,
        source: items[0].source,
        num_order: totalOrders,
        origin: items[0].origin,
      });
    });
  });

  return result;
}

/**
 * Aggregates dates array based on frequency
 */
export function aggregateDates(
  dates: string[],
  frequency: 'daily' | 'weekly' | 'monthly'
): string[] {
  if (frequency === 'daily') {
    return dates;
  }

  const dateSet = new Set<string>();

  dates.forEach(dateStr => {
    const date = parseISO(dateStr);
    const key = frequency === 'weekly'
      ? format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      : format(startOfMonth(date), 'yyyy-MM-dd');
    
    dateSet.add(key);
  });

  return Array.from(dateSet).sort();
}

/**
 * Formats date for display based on frequency
 */
export function formatDateByFrequency(
  dateStr: string,
  frequency: 'daily' | 'weekly' | 'monthly'
): string {
  const date = parseISO(dateStr);

  switch (frequency) {
    case 'daily':
      return format(date, 'MMM dd');
    case 'weekly':
      return format(date, "'W'II yyyy");
    case 'monthly':
      return format(date, 'MMM yyyy');
    default:
      return format(date, 'MMM dd');
  }
}
